// src/lib/partner/partnerMemberInvitations.js
import { supabase } from "../../supabaseClient";

const VALID_PARTNER_ROLES = [
  "owner",
  "admin",
  "cohort_manager",
  "facilitator",
  "viewer",
];

const INVITATION_EXPIRY_DAYS = 7;
const INVITATION_HISTORY_DAYS = 30;
const INVITATION_HISTORY_MS =
  INVITATION_HISTORY_DAYS *
  24 *
  60 *
  60 *
  1000;

function cleanEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function cleanName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function cleanRole(value) {
  const role = String(
    value || ""
  )
    .trim()
    .toLowerCase();

  if (
    !VALID_PARTNER_ROLES.includes(
      role
    )
  ) {
    throw new Error(
      "Please select a valid partner team role."
    );
  }

  return role;
}

function isWithinInvitationHistoryWindow(
  invitation
) {
  const timestamp =
    invitation?.created_at;

  if (!timestamp) {
    return false;
  }

  const sentAt = new Date(timestamp);

  if (
    Number.isNaN(
      sentAt.getTime()
    )
  ) {
    return false;
  }

  return (
    Date.now() - sentAt.getTime() <=
    INVITATION_HISTORY_MS
  );
}

async function getFunctionErrorMessage(
  error
) {
  let message =
    error?.message ||
    "The invitation email could not be sent.";

  const response =
    error?.context;

  if (
    response &&
    typeof response.clone ===
      "function"
  ) {
    try {
      const payload =
        await response
          .clone()
          .json();

      message =
        payload?.error ||
        payload?.message ||
        message;
    } catch {
      // Keep the original Functions error message.
    }
  }

  return message;
}

export async function sendPartnerMemberInvitation({
  partnerId,
  name = "",
  email,
  role,
}) {
  if (!partnerId) {
    throw new Error(
      "The partner organisation could not be identified."
    );
  }

  const cleanedName =
    cleanName(name);

  if (
    cleanedName.length > 100
  ) {
    throw new Error(
      "The team member's full name cannot exceed 100 characters."
    );
  }

  const cleanedEmail =
    cleanEmail(email);

  if (
    !cleanedEmail ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      cleanedEmail
    )
  ) {
    throw new Error(
      "Please enter a valid email address."
    );
  }

  const cleanedRole =
    cleanRole(role);

  const invocationPromise =
    supabase.functions.invoke(
      "send-partner-member-invite",
      {
        body: {
          partnerId,

          name:
            cleanedName ||
            undefined,

          email:
            cleanedEmail,

          role:
            cleanedRole,

          expiresInDays:
            INVITATION_EXPIRY_DAYS,
        },
      }
    );

  const timeoutPromise =
    new Promise(
      (_, reject) => {
        window.setTimeout(
          () => {
            reject(
              new Error(
                "The invitation service took too long to respond. Check the Edge Function logs before trying again."
              )
            );
          },
          30000
        );
      }
    );

  const {
    data,
    error,
  } = await Promise.race([
    invocationPromise,
    timeoutPromise,
  ]);

  if (error) {
    throw new Error(
      await getFunctionErrorMessage(
        error
      )
    );
  }

  if (!data?.success) {
    throw new Error(
      data?.error ||
        "The invitation could not be sent."
    );
  }

  return data;
}

export async function fetchPartnerMemberInvitations(
  partnerId = null
) {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_partner_member_invitations",
    {
      p_partner_id:
        partnerId || null,
    }
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data
        .filter(
          isWithinInvitationHistoryWindow
        )
        .map(
          (invitation) => ({
            ...invitation,

            is_expired:
              Boolean(
                invitation.is_expired
              ),

            can_revoke:
              Boolean(
                invitation.can_revoke
              ),
          })
        )
    : [];
}

export async function getPartnerMemberInviteContext(
  token
) {
  const cleanToken =
    String(
      token || ""
    ).trim();

  if (!cleanToken) {
    throw new Error(
      "The partner invitation token is missing."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_member_invite_context",
    {
      p_token:
        cleanToken,
    }
  );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "The partner invitation could not be found."
    );
  }

  return {
    ...data,

    can_accept:
      Boolean(
        data.can_accept
      ),
  };
}

export async function acceptPartnerMemberInvitation(
  token
) {
  const cleanToken =
    String(
      token || ""
    ).trim();

  if (!cleanToken) {
    throw new Error(
      "The partner invitation token is missing."
    );
  }

  const {
    data: acceptData,
    error: acceptError,
  } = await supabase.rpc(
    "accept_partner_member_invitation",
    {
      p_token: cleanToken,
    }
  );

  if (acceptError) {
    throw acceptError;
  }

  if (!acceptData?.success) {
    throw new Error(
      acceptData?.message ||
        "The partner invitation could not be accepted."
    );
  }

  return acceptData;
}

export async function revokePartnerMemberInvitation(
  invitationId
) {
  if (!invitationId) {
    throw new Error(
      "The invitation could not be identified."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "revoke_partner_member_invitation",
    {
      p_invitation_id:
        invitationId,
    }
  );

  if (error) {
    throw error;
  }

  return data;
}
